import { isEmpty, isEqual, isNil } from 'lodash';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { JsonStorage } from '@shared/util/storage/JsonStorage';
import {
  type EditableViewTemplate,
  getEditableViewTemplate,
  getViewTemplateStorageKey,
} from './EditableViewTemplate';
import { useViewTemplateActions, useViewTemplateState } from './ViewTemplateContext';
import {
  type ViewTemplateQueryParamOverrides,
  clearViewTemplateQueryParamOverrides,
  getViewTemplateQueryParamSyncUpdates,
  useSyncQueryParams,
} from './collectionViewQueryParamOverrides';

// Backup only - see the matching comment on COLLECTION_CRITERIA_TTL in
// PreserveCollectionCriteria.tsx; the URL (kept in sync below) is the primary carrier of
// unsaved edits.
const VIEW_TEMPLATE_TTL = 1000 * 60 * 60 * 24; // 1 day

interface PreserveViewTemplateProps {
  setIsModified: Dispatch<SetStateAction<boolean>>;
  /**
   * Top-level view template field overrides read from query params at mount (see
   * `useViewTemplateQueryParamOverrides` for which fields can be overridden and when). Applied
   * generically on top of the current view template, taking precedence over any localStorage
   * override.
   */
  queryParamOverrides: ViewTemplateQueryParamOverrides;
}

export function PreserveViewTemplate({
  setIsModified,
  queryParamOverrides,
}: PreserveViewTemplateProps) {
  const { viewTemplate, savedViewTemplate, preserveEdits } = useViewTemplateState();
  const { setViewTemplate } = useViewTemplateActions();
  const storageKey = getViewTemplateStorageKey(viewTemplate.id);
  const hasQueryParamOverrides = !isEmpty(queryParamOverrides);
  const syncQueryParams = useSyncQueryParams();

  useEffect(() => {
    const edited = getEditableViewTemplate(viewTemplate);
    const saved = getEditableViewTemplate(savedViewTemplate);
    const isChanged = !isEqual(edited, saved);
    setIsModified(isChanged);
    // Only persist unsaved edits to local storage/the URL for usages that opt in (e.g. the
    // primary collection view page, and only while it's the plain route actually being viewed -
    // see `CollectionViewContainer`). Other usages should only ever reflect the filters passed in.
    if (!preserveEdits) {
      // Actively clear rather than just skipping - `preserveEdits` can flip false after these
      // params were already written (e.g. navigating to a subroute), and nothing else clears them.
      syncQueryParams(clearViewTemplateQueryParamOverrides());
      return;
    }
    if (isChanged) {
      // while changing views, can sometimes get a false positive, so delay saving
      const timer = setTimeout(() => {
        JsonStorage.set(storageKey, edited, VIEW_TEMPLATE_TTL);
        syncQueryParams(getViewTemplateQueryParamSyncUpdates(viewTemplate));
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Back in sync with the saved view - clear both the backup and the address bar so there's
      // no stale override left representing a deviation that no longer exists.
      JsonStorage.remove(storageKey);
      syncQueryParams(clearViewTemplateQueryParamOverrides());
    }
  }, [viewTemplate, savedViewTemplate, storageKey, setIsModified, preserveEdits, syncQueryParams]);

  useEffect(() => {
    if (!preserveEdits || hasQueryParamOverrides) {
      // A query param override (applied below) takes precedence over any localStorage override.
      return;
    }
    // load viewTemplate from storage
    async function loadStoredViewTemplate() {
      const storedViewTemplate = await JsonStorage.get<EditableViewTemplate>(storageKey);
      if (!isNil(storedViewTemplate)) {
        setViewTemplate(prev => ({ ...prev, ...storedViewTemplate }));
      }
    }
    loadStoredViewTemplate();
  }, [storageKey, setViewTemplate, preserveEdits, hasQueryParamOverrides]);

  useEffect(() => {
    if (!preserveEdits || !hasQueryParamOverrides) {
      return;
    }
    const nextViewTemplate = { ...viewTemplate, ...queryParamOverrides };
    if (isEqual(nextViewTemplate, viewTemplate)) {
      // Already applied on a prior run of this effect - avoid redundant persistence below.
      return;
    }
    setViewTemplate(nextViewTemplate);
    // Persist immediately (bypassing the debounce in the effect above), rather than waiting for
    // the isModified/sync cycle to catch up - otherwise a fast refresh right after opening the
    // link could land before the debounced sync fires and lose the override. Unlike before, the
    // override params are left in the URL rather than stripped - they're only ever read once at
    // mount (see `useViewTemplateQueryParamOverrides`), and leaving them in place means the URL
    // stays a durable, shareable/bookmarkable record of what's being viewed.
    JsonStorage.set(storageKey, getEditableViewTemplate(nextViewTemplate), VIEW_TEMPLATE_TTL);
    syncQueryParams(getViewTemplateQueryParamSyncUpdates(nextViewTemplate));
  }, [
    preserveEdits,
    hasQueryParamOverrides,
    queryParamOverrides,
    viewTemplate,
    setViewTemplate,
    storageKey,
    syncQueryParams,
  ]);

  return null;
}
